/**
 * One-Euro filter — адаптивный low-pass для шумных сигналов (wrist keypoints, touch).
 * https://gery.casiez.net/1euro/
 *
 * minCutoff (Hz): ниже → сильнее сглаживает в покое.
 * beta: выше → быстрее отпускает при резком движении.
 */
export class OneEuroFilter {
  private xPrev: number | null = null;

  private dxPrev = 0;

  private tPrevSec: number | null = null;

  constructor(
    private readonly minCutoff = 1.0,
    private readonly beta = 0.007,
    private readonly dCutoff = 1.0,
  ) {}

  reset(): void {
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrevSec = null;
  }

  filter(x: number, tSec: number): number {
    if (!Number.isFinite(x)) return x;
    if (this.tPrevSec === null || this.xPrev === null) {
      this.tPrevSec = tSec;
      this.xPrev = x;
      return x;
    }
    const dt = Math.max(1e-3, tSec - this.tPrevSec);
    const dx = (x - this.xPrev) / dt;
    const aD = OneEuroFilter.alpha(dt, this.dCutoff);
    const dxHat = aD * dx + (1 - aD) * this.dxPrev;
    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const a = OneEuroFilter.alpha(dt, cutoff);
    const xHat = a * x + (1 - a) * this.xPrev;
    this.xPrev = xHat;
    this.dxPrev = dxHat;
    this.tPrevSec = tSec;
    return xHat;
  }

  private static alpha(dt: number, cutoff: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }
}

/** Пара фильтров для точки (x, y) — удобный контейнер. */
export class OneEuroPoint {
  readonly fx: OneEuroFilter;

  readonly fy: OneEuroFilter;

  constructor(minCutoff = 1.0, beta = 0.007, dCutoff = 1.0) {
    this.fx = new OneEuroFilter(minCutoff, beta, dCutoff);
    this.fy = new OneEuroFilter(minCutoff, beta, dCutoff);
  }

  filter(x: number, y: number, tSec: number): { x: number; y: number } {
    return { x: this.fx.filter(x, tSec), y: this.fy.filter(y, tSec) };
  }

  reset(): void {
    this.fx.reset();
    this.fy.reset();
  }
}
